import type { AxiosInstance } from "axios";
import type { QueryClient } from "@tanstack/react-query";
import { USER_API } from "config";
import { projectKeys } from "queries/project.keys";
import type { ProjectPayload } from "./useProjects.types";
import { findCachedProjectPayload } from "utils/permissions/parseProjectPermissions";

export type FetchProjectDetailOptions = {
  includeVersions?: boolean;
  documentIdForFeatureStatuses?: string | number;
};

export const unwrapProjectApiPayload = <T>(value: unknown): T => {
  if (
    value &&
    typeof value === "object" &&
    (value as { data?: unknown }).data &&
    !Array.isArray((value as { data?: unknown }).data)
  ) {
    return (value as { data: T }).data;
  }
  return value as T;
};

export const fetchProjectDetail = async (
  axios: AxiosInstance,
  projectId: string,
  options: FetchProjectDetailOptions = {}
): Promise<ProjectPayload> => {
  const includeVersions = options.includeVersions ?? true;
  const documentScope =
    options.documentIdForFeatureStatuses != null &&
    String(options.documentIdForFeatureStatuses) !== "" &&
    String(options.documentIdForFeatureStatuses) !== "0"
      ? String(options.documentIdForFeatureStatuses)
      : null;

  const queryParts = [
    includeVersions ? "include_versions=true" : null,
    documentScope ? `document_id=${encodeURIComponent(documentScope)}` : null,
  ].filter((part): part is string => Boolean(part));

  const query = queryParts.length > 0 ? `?${queryParts.join("&")}` : "";
  const response = await axios.get<ProjectPayload>(`${USER_API.projects}/${projectId}${query}`);
  return unwrapProjectApiPayload<ProjectPayload>(response);
};

export const getProjectDetailQueryKey = (
  projectId: string,
  documentIdForFeatureStatuses?: string | number
) =>
  documentIdForFeatureStatuses != null &&
  String(documentIdForFeatureStatuses) !== "" &&
  String(documentIdForFeatureStatuses) !== "0"
    ? projectKeys.detailForDocument(projectId, documentIdForFeatureStatuses)
    : projectKeys.detail(projectId);

export const readCachedProjectDetail = (
  queryClient: QueryClient,
  projectId: string
): ProjectPayload | null => {
  const cached = findCachedProjectPayload(
    (filters) => queryClient.getQueriesData(filters),
    projectId
  );
  return cached ? unwrapProjectApiPayload<ProjectPayload>(cached) : null;
};
